import { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { SearchIcon } from './icons.jsx'
import { useManifest } from '../lib/data.jsx'
import { useSearchCompanies, searchAll } from '../lib/appSearch.js'
import { useSlashFocus } from '../lib/useSlashFocus.js'
import { useRbac } from '../lib/rbac.js'
import { ResultIcon, Highlight } from './searchUi.jsx'

// Global search: an input in the topbar that opens a live preview dropdown of
// sectioned results (Leads, Markets, Pages) as you type.
// The same sections come from lib/appSearch, so /search mirrors them exactly.
// "View all results" (or Enter with nothing highlighted) opens the full page.
const PER_SECTION = 5

export default function GlobalSearch() {
  const navigate = useNavigate()
  const { manifest } = useManifest()
  const [query, setQuery] = useState('')
  const [debounced, setDebounced] = useState('')
  const [focused, setFocused] = useState(false)
  // -1 = nothing highlighted: Enter opens the full results page. Arrows pick items.
  const [active, setActive] = useState(-1)
  const inputRef = useRef(null)
  const rootRef = useRef(null)
  useSlashFocus(inputRef) // "/" focuses the global search

  // Load the (large) company index only once the user engages with search.
  const companies = useSearchCompanies(focused || query.length > 0)

  // Debounce the query that drives the full-database scan.
  useEffect(() => {
    const t = setTimeout(() => setDebounced(query.trim()), 110)
    return () => clearTimeout(t)
  }, [query])

  const { can } = useRbac()
  const { sections, total } = useMemo(
    () => searchAll(debounced, { companies, manifest, perSection: PER_SECTION, can }),
    [debounced, companies, manifest, can],
  )
  const flat = useMemo(() => sections.flatMap((s) => s.items), [sections])

  // Reset the keyboard cursor whenever the result set changes.
  useEffect(() => setActive(-1), [debounced, flat.length])

  const q = query.trim()
  const open = focused && q.length > 0
  const loadingLeads = open && companies === null

  function go(item) {
    setFocused(false)
    setQuery('')
    inputRef.current?.blur()
    navigate(item.to, item.state ? { state: item.state } : undefined)
  }

  function viewAll() {
    if (!q) return
    setFocused(false)
    inputRef.current?.blur()
    navigate(`/search?q=${encodeURIComponent(q)}`)
  }

  // Keyboard: ↑/↓ move across all items (the extra slot at flat.length is the
  // "View all results" row); Enter opens the highlighted item or the full page;
  // Esc closes.
  function onKeyDown(e) {
    if (!open) {
      if (e.key === 'Enter') {
        e.preventDefault()
        viewAll()
      }
      return
    }
    if (e.key === 'ArrowDown') {
      e.preventDefault()
      setActive((a) => Math.min(a + 1, flat.length))
    } else if (e.key === 'ArrowUp') {
      e.preventDefault()
      setActive((a) => Math.max(a - 1, -1))
    } else if (e.key === 'Enter') {
      e.preventDefault()
      if (active >= 0 && active < flat.length) go(flat[active])
      else viewAll()
    } else if (e.key === 'Escape') {
      setFocused(false)
      inputRef.current?.blur()
    }
  }

  // Close on outside click.
  useEffect(() => {
    if (!open) return
    function onDoc(e) {
      if (rootRef.current && !rootRef.current.contains(e.target)) setFocused(false)
    }
    document.addEventListener('mousedown', onDoc)
    return () => document.removeEventListener('mousedown', onDoc)
  }, [open])

  // Running index so the keyboard cursor maps across flattened sections.
  let cursor = -1

  return (
    <div
      ref={rootRef}
      className="absolute left-[calc(50vw-var(--sidebar-w))] top-1/2 w-[min(400px,36vw)] -translate-x-1/2 -translate-y-1/2 max-[760px]:hidden"
    >
      <div
        className="flex items-center gap-x-2 rounded-[10px] border border-solid border-[#cbd5e1] bg-surface px-[14px] py-1.5 text-dim transition-[border-color,box-shadow] duration-150 focus-within:border-primary focus-within:shadow-[0_0_0_3px_rgba(60,135,247,0.2)] dark:border-line"
        role="search"
      >
        <SearchIcon className="h-4 w-4 flex-none" />
        <input
          ref={inputRef}
          type="search"
          className="min-w-0 flex-1 border-none bg-transparent text-[14px] text-ink outline-none placeholder:text-faint"
          placeholder="Search…"
          aria-label="Search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          onFocus={() => setFocused(true)}
          onKeyDown={onKeyDown}
        />
        {/* Keyboard-shortcut hint, right-aligned. A fixed square (not padded
            text) so the key reads as a key — sized off the glyph's own box,
            with a tighter radius than a pill. */}
        <kbd className="grid h-[26px] w-[26px] flex-none place-items-center rounded-[7px] border border-line bg-panel text-[12px] font-semibold leading-none text-faint">
          /
        </kbd>
      </div>

      {open && (
        <div className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[min(70vh,560px)] overflow-y-auto rounded-[12px] border border-line bg-surface py-1.5 shadow-[0_16px_40px_rgba(0,0,0,0.22)]">
          {sections.length === 0 && !loadingLeads ? (
            <div className="px-4 py-6 text-center text-[13px] text-dim">No matches for “{q}”</div>
          ) : (
            sections.map((section) => (
              <div key={section.key} className="pb-1">
                <div className="px-4 pb-1 pt-2 text-[11px] font-semibold uppercase tracking-[0.06em] text-faint">
                  {section.label}
                </div>
                {section.items.map((item) => {
                  cursor += 1
                  const index = cursor
                  return (
                    <button
                      key={item.id}
                      type="button"
                      className={`flex w-full items-center gap-x-3 px-3 py-2 text-left transition-colors ${
                        index === active ? 'bg-panel' : 'bg-transparent'
                      }`}
                      onMouseEnter={() => setActive(index)}
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => go(item)}
                    >
                      <span className="grid h-8 w-8 flex-none place-items-center rounded-[8px] bg-panel text-faint [&_svg]:h-4 [&_svg]:w-4">
                        <ResultIcon icon={item.icon} />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-[14px] text-ink">
                          <Highlight text={item.title} query={q} />
                        </span>
                        {item.subtitle && (
                          <span className="block truncate text-[12.5px] text-dim">
                            <Highlight text={item.subtitle} query={q} />
                          </span>
                        )}
                      </span>
                    </button>
                  )
                })}
              </div>
            ))
          )}

          {loadingLeads && (
            <div className="px-4 py-2 text-[12.5px] text-faint">Searching leads…</div>
          )}

          {(() => {
            cursor += 1
            const index = cursor
            return (
              <button
                type="button"
                className={`mt-0.5 flex w-full items-center justify-between gap-x-3 [border-top:1px_solid_var(--border)] px-4 py-2.5 text-left transition-colors ${
                  index === active ? 'bg-panel' : 'bg-transparent'
                }`}
                onMouseEnter={() => setActive(index)}
                onMouseDown={(e) => e.preventDefault()}
                onClick={viewAll}
              >
                <span className="text-[13px] font-semibold text-primary-dark">
                  View all results{total ? ` (${total.toLocaleString()})` : ''}
                </span>
                <span className="text-[13px] text-faint">↵</span>
              </button>
            )
          })()}
        </div>
      )}
    </div>
  )
}
